import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { CallRecordsService } from '../../../core/services/call-records/call-records.service';
import { AlertService } from '../../../core/services/alert/alert.service';
import { CallContactResource, CallRecentResource } from '../../../core/interfaces/communication.interface';

@Component({
  selector: 'app-contacts-agenda',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './contacts-agenda.component.html',
  styleUrl: './contacts-agenda.component.css',
})
export class ContactsAgendaComponent implements OnInit {
  @Output() backButtonClick = new EventEmitter<void>();
  @Output() callContact = new EventEmitter<{ phone: string; name: string; client_reference?: string }>();

  contacts: CallContactResource[] = [];
  recents: CallRecentResource[] = [];
  searchText = '';
  activeTab: 'all' | 'favorites' | 'recents' = 'all';
  isLoading = false;

  constructor(
    private callRecordsService: CallRecordsService,
    private alertService: AlertService,
  ) {}

  ngOnInit(): void {
    this.loadContacts();
  }

  emitBack(): void {
    this.backButtonClick.emit();
  }

  setTab(tab: 'all' | 'favorites' | 'recents'): void {
    this.activeTab = tab;
    if (tab === 'recents') {
      this.loadRecents();
    } else {
      this.loadContacts();
    }
  }

  loadContacts(): void {
    this.isLoading = true;
    const params: any = {};
    if (this.searchText) params.search = this.searchText;
    if (this.activeTab === 'favorites') params.favorites = true;

    this.callRecordsService.getContacts(params).subscribe({
      next: (res) => { this.contacts = res.data || []; this.isLoading = false; },
      error: () => { this.contacts = []; this.isLoading = false; },
    });
  }

  loadRecents(): void {
    this.isLoading = true;
    this.callRecordsService.getRecents().subscribe({
      next: (res) => { this.recents = res.data || []; this.isLoading = false; },
      error: () => { this.recents = []; this.isLoading = false; },
    });
  }

  onSearchChange(): void {
    if (this.activeTab === 'recents') return; // Recents don't support search
    this.loadContacts();
  }

  onCallContact(contact: CallContactResource): void {
    if (!contact.phone_e164) return;
    this.callContact.emit({ phone: contact.phone_e164, name: contact.name, client_reference: contact.reference });
  }

  onCallRecent(recent: CallRecentResource): void {
    this.callContact.emit({
      phone: recent.number,
      name: recent.name || recent.client?.name || '',
      client_reference: recent.client?.reference,
    });
  }

  toggleFavorite(contact: CallContactResource): void {
    const wasAdding = !contact.is_favorite;
    const obs = contact.is_favorite
      ? this.callRecordsService.removeFavorite(contact.reference)
      : this.callRecordsService.addFavorite(contact.reference);

    obs.subscribe({
      next: (res) => {
        contact.is_favorite = res.data?.is_favorite ?? !contact.is_favorite;
        if (wasAdding && contact.is_favorite) {
          this.alertService.successOk('communication.voip.favorite_added');
        } else if (!wasAdding && !contact.is_favorite) {
          this.alertService.successOk('communication.voip.favorite_removed');
        }
      },
    });
  }

  getInitial(name: string | null): string {
    return (name || '?').charAt(0).toUpperCase();
  }

  formatLastCall(dateStr: string | null): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
  }

  getDirectionIcon(direction: string): string {
    return direction === 'inbound' ? 'icon-phone-incoming' : 'icon-phone-outgoing';
  }

  getStatusClass(status: string): string {
    const map: Record<string, string> = {
      answered: 'text-success', missed: 'text-danger', busy: 'text-warning', failed: 'text-danger',
    };
    return map[status] || 'text-secondary';
  }
}
